import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { api, API_BASE, downloadFile } from "@/lib/api";
import { fmtEUR, fmtDate, fmtDateTime } from "@/lib/format";
import { leesOverzichtNav, usePijltjesNav } from "@/lib/overzichtNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, ChevronLeft, ChevronRight, CalendarDays, Plus, Trash2, Pencil,
  Package as PackageIcon, FileDown, Mail, ExternalLink, Phone, PhoneCall, User, FileText, XCircle, Trophy, CheckCircle2, MapPin, Truck,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PakketStatusBadge, usePakketSync } from "@/components/PakketStatusBadge";
import { BelletjesPanel } from "@/components/BelletjesPanel";
import { BtwOverzicht } from "@/components/BtwOverzicht";

// Pipeline fases (Afgevallen is een aparte abort, niet in de slider)
const FASES = ["Cold", "Terugbellen", "Sample opsturen", "Sample opgestuurd", "Onderhandeling", "Deal!", "Afgevallen"];
const PIPELINE = ["Cold", "Terugbellen", "Sample opsturen", "Sample opgestuurd", "Onderhandeling", "Deal!"];
const FASE_KANS = { "Cold": 0, "Terugbellen": 5, "Sample opsturen": 20, "Sample opgestuurd": 35, "Onderhandeling": 45, "Deal!": 100, "Afgevallen": 0 };

function FaseStepper({ value, onChange }) {
  const isAfgevallen = value === "Afgevallen";
  const idx = isAfgevallen ? -1 : Math.max(0, PIPELINE.indexOf(value || "Cold"));
  const pct = isAfgevallen ? 0 : Math.round((idx / (PIPELINE.length - 1)) * 100);
  const isDeal = value === "Deal!";

  return (
    <div className="space-y-3" data-testid="fase-stepper">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase text-muted-foreground tracking-wide">Verkoopproces</span>
          {isDeal && <Trophy className="h-4 w-4 text-accent" data-testid="deal-trophy" />}
          {isAfgevallen && <XCircle className="h-4 w-4 text-destructive" />}
        </div>
        <div className="flex items-center gap-3">
          <span className={`font-display text-2xl tabular-nums ${isDeal ? "text-accent" : isAfgevallen ? "text-destructive" : ""}`} data-testid="fase-pct">
            {isAfgevallen ? "—" : `${pct}%`}
          </span>
          {!isAfgevallen ? (
            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                    onClick={()=>onChange("Afgevallen")} data-testid="markeer-afgevallen">
              <XCircle className="h-4 w-4 mr-1" />Markeer afgevallen
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={()=>onChange("Cold")} data-testid="herstart-kans">
              Heractiveer
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className={`h-2 w-full rounded-full ${isAfgevallen ? "bg-destructive/15" : "bg-secondary"} overflow-hidden`}>
        <div className={`h-full transition-all duration-500 ${isDeal ? "bg-accent" : isAfgevallen ? "bg-destructive" : "bg-primary"}`}
             style={{ width: isAfgevallen ? "100%" : `${pct}%` }} />
      </div>

      {/* Klikbare stappen */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
        {PIPELINE.map((fase, i) => {
          const active = !isAfgevallen && i === idx;
          const passed = !isAfgevallen && i < idx;
          const isDealStep = fase === "Deal!";
          return (
            <button key={fase} type="button"
              onClick={() => onChange(fase)}
              data-testid={`fase-step-${fase}`}
              className={`group text-left p-2 rounded-md border transition-all ${
                active ? "border-primary bg-primary/10 shadow-sm"
                : passed ? "border-primary/50 bg-primary/5"
                : isAfgevallen ? "border-border opacity-60"
                : "border-border hover:border-primary/40 hover:bg-secondary/50"
              } ${isDealStep && active ? "border-accent bg-accent/10 ring-2 ring-accent/30" : ""}`}>
              <div className="flex items-center gap-1.5">
                <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold ${
                  active ? (isDealStep ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground")
                  : passed ? "bg-primary/30 text-primary-foreground"
                  : "bg-secondary text-muted-foreground"
                }`}>{passed ? "✓" : i + 1}</span>
                <span className={`text-xs font-medium truncate ${active ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"}`}>{fase}</span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{FASE_KANS[fase]}%</div>
            </button>
          );
        })}
      </div>

      {/* Gamification ribbon bij Deal */}
      {isDeal && (
        <div className="bg-gradient-to-r from-accent/15 via-accent/10 to-transparent border border-accent/30 rounded-lg p-3 flex items-center gap-3" data-testid="deal-banner">
          <Trophy className="h-6 w-6 text-accent" />
          <div>
            <div className="font-medium">Deal gewonnen! 🎉</div>
            <div className="text-xs text-muted-foreground">Maak een pakbon om de levering &amp; facturatie te starten.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactCard({ kans, org, contacten, onPick, regels, totaal, onBel }) {
  const selected = contacten.find(c => c.id === kans.contactpersoon_id) || null;
  const naamLabel = selected ? `${selected.voornaam} ${selected.achternaam}` : "";

  const offerteHref = useMemo(() => {
    if (!selected?.email) return null;
    const lines = [
      `Beste ${selected.voornaam || ""},`, "",
      `Hierbij onze offerte voor ${kans.naam || "uw aanvraag"}:`, "",
    ];
    regels.forEach(r => {
      const subT = (Number(r.aantal)||0) * (Number(r.verkoopprijs)||0);
      lines.push(`• ${r.aantal}× ${r.product} — € ${subT.toFixed(2).replace(".",",")}`);
    });
    if (!regels.length) lines.push(`• Bedrag: € ${Number(kans.bedrag||0).toFixed(2).replace(".",",")}`);
    lines.push("", `Totaal: € ${Number(totaal||kans.bedrag||0).toFixed(2).replace(".",",")}`, "",
      "Heeft u vragen of wilt u akkoord geven, dan hoor ik het graag.", "",
      "Met vriendelijke groet,");
    const subject = `Offerte ${kans.naam || ""}`.trim();
    const body = lines.join("\n");
    return `mailto:${selected.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [selected, kans, regels, totaal]);

  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="contact-card">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase text-muted-foreground tracking-wide">Contact</div>
        {org && <Link to={`/organisaties/${org.id}`} className="text-xs text-primary hover:underline">Beheer org →</Link>}
      </div>

      <div>
        <Label className="text-xs text-muted-foreground">Contactpersoon</Label>
        <Select value={kans.contactpersoon_id || "none"} onValueChange={(v)=>onPick(v === "none" ? null : v)}>
          <SelectTrigger data-testid="contact-picker"><SelectValue placeholder="— geen —" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— geen contactpersoon —</SelectItem>
            {contacten.map(c => (
              <SelectItem key={c.id} value={c.id}>
                {c.voornaam} {c.achternaam}{c.functie ? ` — ${c.functie}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selected ? (
        <div className="space-y-2 pt-1">
          <div className="flex items-center justify-between gap-2 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <User className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="font-medium truncate">{naamLabel}</span>
              {selected.functie && <span className="text-xs text-muted-foreground shrink-0">· {selected.functie}</span>}
            </div>
            {!(selected.telefoon || selected.mobiel) && (
              <Button size="sm" onClick={onBel} data-testid="contact-bel-btn" className="shrink-0">
                <Phone className="h-4 w-4 mr-1" /> Bel
              </Button>
            )}
          </div>

          {(selected.telefoon || selected.mobiel) && (
            <div className="flex items-center justify-between gap-2 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex flex-col">
                  {selected.telefoon && (
                    <a href={`tel:${selected.telefoon.replace(/\s/g,"")}`}
                       className="text-primary hover:underline" data-testid="contact-tel">{selected.telefoon}</a>
                  )}
                  {selected.mobiel && (
                    <a href={`tel:${selected.mobiel.replace(/\s/g,"")}`}
                       className="text-primary hover:underline text-xs" data-testid="contact-mob">{selected.mobiel} (mobiel)</a>
                  )}
                </div>
              </div>
              <Button size="sm" onClick={onBel} data-testid="contact-bel-btn" className="shrink-0">
                <Phone className="h-4 w-4 mr-1" /> Bel
              </Button>
            </div>
          )}

          {selected.email && (
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
              <a href={`mailto:${selected.email}`} className="text-primary hover:underline truncate" data-testid="contact-mail">
                {selected.email}
              </a>
            </div>
          )}

          <div className="pt-2 border-t border-border space-y-2">
            {offerteHref ? (
              <a href={offerteHref} data-testid="mail-offerte"
                 className="inline-flex items-center justify-center w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-md px-3 py-2 text-sm font-medium transition-colors">
                <FileText className="h-4 w-4 mr-2" /> Mail offerte
              </a>
            ) : (
              <div className="text-xs text-muted-foreground italic">Geen e-mailadres — voeg een mailadres toe aan het contact om een offerte te kunnen mailen.</div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic border border-dashed border-border rounded-md p-3">
          Selecteer een contactpersoon hierboven om snel te bellen, mailen of een offerte te versturen.
        </div>
      )}
    </div>
  );
}

const toDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toISOString().slice(0, 10);
};

function RegelDialog({ parentId, relatieId, value, onSaved, trigger }) {
  const empty = { code: "", product: "", aantal: 1, inkoopprijs: 0, verkoopprijs: 0, is_klantprijs: false, standaard_verkoopprijs: null };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(value || empty);
  const [producten, setProducten] = useState([]);
  const [klantPrijzen, setKlantPrijzen] = useState({}); // product_id -> custom
  const [zoek, setZoek] = useState("");
  const [openSugg, setOpenSugg] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (open) {
      setF(value || empty);
      setZoek(""); setOpenSugg(false);
      api.get("/producten").then(r => setProducten(r.data));
      if (relatieId) api.get(`/organisaties/${relatieId}/prijzen`).then(r => {
        setKlantPrijzen(Object.fromEntries(r.data.map(x => [x.product_id, x])));
      }).catch(()=>{});
    }
    /* eslint-disable-next-line */
  }, [open]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenSugg(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const suggesties = useMemo(() => {
    const t = zoek.trim().toLowerCase();
    if (!t) return [];
    return producten.filter(p =>
      (p.naam || "").toLowerCase().includes(t) || (p.sku || "").toLowerCase().includes(t)
    ).slice(0, 8);
  }, [zoek, producten]);

  const pickProduct = (sku) => {
    const p = producten.find(x => x.sku === sku);
    if (!p) return;
    const cust = klantPrijzen[p.id];
    const vp = cust && cust.verkoopprijs != null ? Number(cust.verkoopprijs) : Number(p.verkoopprijs || 0);
    const ip = cust && cust.inkoopprijs != null ? Number(cust.inkoopprijs) : Number(p.inkoopprijs || 0);
    setF({ ...f, code: p.sku, product: p.naam,
           inkoopprijs: ip, verkoopprijs: vp,
           is_klantprijs: !!cust,
           standaard_verkoopprijs: Number(p.verkoopprijs || 0) });
    setZoek(""); setOpenSugg(false);
  };

  const submit = async () => {
    if (!f.product.trim()) { toast.error("Product is verplicht"); return; }
    try {
      const num = (v) => parseFloat(String(v).toString().replace(",", ".")) || 0;
      const payload = { ...f, aantal: num(f.aantal), inkoopprijs: num(f.inkoopprijs), verkoopprijs: num(f.verkoopprijs) };
      if (value?.id) await api.patch(`/verkoopkansregels/${value.id}`, payload);
      else await api.post("/verkoopkansregels", { ...payload, verkoopkans_id: parentId });
      toast.success("Regel opgeslagen"); setOpen(false); onSaved();
    } catch { toast.error("Opslaan mislukt"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{value ? "Regel bewerken" : "Nieuwe regel"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div ref={boxRef} className="relative">
            <Label>Artikel uit catalogus</Label>
            <Input
              value={zoek}
              placeholder="Zoek op productnaam of SKU…"
              onFocus={() => setOpenSugg(true)}
              onChange={(e) => { setZoek(e.target.value); setOpenSugg(true); }}
              data-testid="reg-product-picker"
            />
            {openSugg && suggesties.length > 0 && (
              <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-72 overflow-y-auto">
                {suggesties.map(p => {
                  const heeftKlantprijs = klantPrijzen[p.id] && klantPrijzen[p.id].verkoopprijs != null;
                  return (
                    <button key={p.id} type="button"
                      className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex justify-between gap-2 items-center"
                      onClick={() => pickProduct(p.sku)}
                      data-testid={`reg-suggestion-${p.sku}`}>
                      <span className="truncate">{p.naam}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        {heeftKlantprijs && <span className="text-[10px] uppercase font-bold bg-primary/15 text-primary rounded px-1 py-0.5">klantprijs</span>}
                        <span className="text-xs text-muted-foreground font-mono">{p.sku}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Code</Label><Input data-testid="reg-code" value={f.code || ""} onChange={(e)=>setF({...f,code:e.target.value})} /></div>
            <div className="col-span-2"><Label>Product</Label><Input data-testid="reg-product" value={f.product || ""} onChange={(e)=>setF({...f,product:e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Aantal</Label><Input type="number" step="0.01" value={f.aantal} onChange={(e)=>setF({...f,aantal:e.target.value})} /></div>
            <div><Label>Inkoop (€)</Label><Input type="number" step="0.01" value={f.inkoopprijs} onChange={(e)=>setF({...f,inkoopprijs:e.target.value})} /></div>
            <div>
              <Label className="flex items-center gap-1.5">Verkoop (€)
                {f.is_klantprijs && <span className="text-[10px] uppercase font-bold bg-primary/15 text-primary rounded px-1.5 py-0.5" data-testid="reg-klantprijs">klantprijs</span>}
              </Label>
              <Input type="number" step="0.01" data-testid="reg-verkoop" value={f.verkoopprijs} onChange={(e)=>setF({...f,verkoopprijs:e.target.value, is_klantprijs: false})} />
              {f.is_klantprijs && f.standaard_verkoopprijs != null && Number(f.standaard_verkoopprijs) !== Number(f.verkoopprijs) && (
                <div className="text-[10px] text-muted-foreground mt-0.5">Standaard: <span className="line-through">€ {Number(f.standaard_verkoopprijs).toFixed(2).replace(".",",")}</span></div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="reg-submit">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NieuweHandmatigeFactuurButton({ kansId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notitie, setNotitie] = useState("");
  const [kopieer, setKopieer] = useState(true);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/verkoopkansen/${kansId}/nieuwe-factuur`,
        { notitie, kopieer_regels: kopieer });
      toast.success(`Nieuwe factuur aangemaakt (${data.factuurnummer || "concept"})`);
      setOpen(false); setNotitie(""); setKopieer(true);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aanmaken mislukt");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="nieuwe-handmatige-factuur-btn">
          <Plus className="h-3 w-3" /> Nieuwe factuur
        </button>
      </DialogTrigger>
      <DialogContent data-testid="nieuwe-handmatige-factuur-dialog">
        <DialogHeader><DialogTitle>Handmatig een nieuwe factuur aanmaken</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Er wordt een extra concept-factuur op deze verkoopkans aangemaakt. Er wordt <strong>geen</strong> pakbon
            gemaakt en <strong>geen</strong> voorraad afgeboekt. Factuurnummer en -datum worden pas gezet bij versturen.
          </p>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={kopieer} onChange={(e)=>setKopieer(e.target.checked)}
                   className="mt-1" data-testid="nieuwe-factuur-kopieer-regels" />
            <span>Kopieer regels van de verkoopkans naar de factuur</span>
          </label>
          <div>
            <Label>Notitie (optioneel)</Label>
            <Textarea value={notitie} onChange={(e)=>setNotitie(e.target.value)} rows={3}
                      placeholder="Interne notitie voor deze extra factuur…"
                      data-testid="nieuwe-factuur-notitie" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} disabled={busy} data-testid="nieuwe-factuur-submit">
            {busy ? "Aanmaken…" : "Factuur aanmaken"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PakbonDialog({ kansId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ leverdatum: toDateInput(new Date().toISOString()), notitie: "", voorraad_bron: "warehouse" });
  const submit = async () => {
    try {
      const payload = { verkoopkans_id: kansId,
                        leverdatum: f.leverdatum ? new Date(f.leverdatum).toISOString() : null,
                        notitie: f.notitie, voorraad_bron: f.voorraad_bron };
      const { data } = await api.post("/pakbonnen", payload);
      toast.success("Pakbon aangemaakt + concept-factuur in wachtrij");
      setOpen(false);
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pakbon aanmaken mislukt");
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="maak-pakbon-btn"><PackageIcon className="h-4 w-4 mr-2" />Maak pakbon</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Pakbon aanmaken</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Leverdatum</Label><Input type="date" data-testid="pakbon-leverdatum" value={f.leverdatum} onChange={(e)=>setF({...f,leverdatum:e.target.value})} /></div>
          <div>
            <Label>Voorraad afboeken van</Label>
            <div className="flex gap-2 mt-1 flex-wrap">
              <Button type="button" variant={f.voorraad_bron==="warehouse"?"default":"outline"} size="sm"
                onClick={()=>setF({...f, voorraad_bron:"warehouse"})} data-testid="pakbon-bron-warehouse">Eigen warehouse</Button>
              <Button type="button" variant={f.voorraad_bron==="leverancier"?"default":"outline"} size="sm"
                onClick={()=>setF({...f, voorraad_bron:"leverancier"})} data-testid="pakbon-bron-leverancier">Leverancier</Button>
              <Button type="button" variant={f.voorraad_bron==="geen"?"default":"outline"} size="sm"
                onClick={()=>setF({...f, voorraad_bron:"geen"})} data-testid="pakbon-bron-geen">Geen (niet afboeken)</Button>
            </div>
            {f.voorraad_bron === "geen" && (
              <p className="text-[11px] text-amber-700 mt-1">Er worden geen voorraadmutaties aangemaakt bij deze pakbon.</p>
            )}
          </div>
          <div><Label>Notitie</Label><Textarea value={f.notitie} onChange={(e)=>setF({...f,notitie:e.target.value})} placeholder="Bijv. afleverinstructies…" /></div>
          <div className="text-xs text-muted-foreground bg-secondary/50 rounded-md p-3">
            Bij aanmaken wordt automatisch een concept-factuur in de <strong>Wachtrij</strong> gezet met dezelfde regels{f.voorraad_bron === "geen" ? "" : ", en wordt de voorraad afgeboekt van het gekozen voorraaddeel"}.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="pakbon-submit">Aanmaken</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DoorzetChannelDockDialog({ kansId, kansReferentie = "", info, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ referentie: "", notitie: "", negeer_voorraad: false });

  useEffect(() => { if (open) setF((p) => ({ ...p, referentie: kansReferentie })); }, [open, kansReferentie]);

  if (info?.cd_order_id) {
    return (
      <div className="text-sm border border-border rounded-md p-3 space-y-1" data-testid="channeldock-status">
        <div className="font-medium flex items-center gap-2"><Truck className="h-4 w-4 text-primary" />Doorgezet naar ChannelDock</div>
        <div className="text-xs text-muted-foreground">Order {info.cd_order_id} · pakbon {info.pakbon_nummer || "—"}</div>
        {info.track_trace ? (
          <a href={info.track_trace_url || "#"} target="_blank" rel="noreferrer"
            className="text-xs text-primary hover:underline" data-testid="channeldock-tt">
            {info.carrier ? `${info.carrier} · ` : ""}{info.track_trace}
          </a>
        ) : <div className="text-xs text-muted-foreground">Nog geen track &amp; trace ontvangen</div>}
      </div>
    );
  }

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/verkoopkansen/${kansId}/doorzetten-channeldock`, f);
      toast.success(`Doorgezet naar ChannelDock · pakbon ${data.pakbon_nummer}`);
      (data.waarschuwingen || []).forEach((w) => toast.warning(w));
      setOpen(false); onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Doorzetten mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" data-testid="doorzetten-channeldock-btn">
          <Truck className="h-4 w-4 mr-2" /> Doorzetten naar ChannelDock
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="doorzetten-channeldock-dialog">
        <DialogHeader><DialogTitle>Order doorzetten naar ChannelDock</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Referentie (optioneel)</Label>
            <Input value={f.referentie} onChange={(e) => setF({ ...f, referentie: e.target.value })}
              placeholder="Bijv. ordernummer" data-testid="cd-doorzet-referentie" /></div>
          <div><Label>Opmerking voor het warehouse (optioneel)</Label>
            <Textarea value={f.notitie} onChange={(e) => setF({ ...f, notitie: e.target.value })}
              data-testid="cd-doorzet-notitie" /></div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.negeer_voorraad}
              onChange={(e) => setF({ ...f, negeer_voorraad: e.target.checked })}
              data-testid="cd-doorzet-negeer" />
            Toch doorzetten bij te weinig ChannelDock-voorraad
          </label>
          <div className="text-xs text-muted-foreground bg-secondary/50 rounded-md p-3">
            Er wordt een pakbon en concept-factuur aangemaakt zonder eigen voorraadmutatie (ChannelDock houdt zijn eigen
            voorraad bij). De verzendmethode kiest ChannelDock zelf; track &amp; trace komt automatisch terug.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} disabled={busy} data-testid="cd-doorzet-verstuur">
            {busy ? "Doorzetten…" : "Doorzetten"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DoorzetLeverancierDialog({ kansId, kansReferentie = "", onDone }) {
  const [open, setOpen] = useState(false);
  const [leveranciers, setLeveranciers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    leverancier_id: "", aanleverdatum: toDateInput(new Date().toISOString()),
    referentie: "", voorraad_bron: "leverancier",
  });

  useEffect(() => {
    if (open) {
      api.get("/leveranciers").then(({ data }) => setLeveranciers(data || [])).catch(() => {});
      setF((prev) => ({ ...prev, referentie: kansReferentie }));
    }
  }, [open, kansReferentie]);

  const submit = async () => {
    if (!f.leverancier_id) { toast.error("Kies een leverancier"); return; }
    setBusy(true);
    try {
      const payload = {
        leverancier_id: f.leverancier_id,
        aanleverdatum: f.aanleverdatum ? new Date(f.aanleverdatum).toISOString() : null,
        referentie: f.referentie, voorraad_bron: f.voorraad_bron,
      };
      const { data } = await api.post(`/verkoopkansen/${kansId}/doorzetten-leverancier`, payload);
      toast.success(`Verstuurd naar ${data.leverancier} · IO ${data.inkooporder_nummer} + pakbon ${data.pakbon_nummer}`);
      setOpen(false); onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Doorzetten mislukt");
    } finally { setBusy(false); }
  };

  const gekozen = leveranciers.find((l) => l.id === f.leverancier_id);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" data-testid="doorzetten-leverancier-btn">
          <ExternalLink className="h-4 w-4 mr-2" /> Doorzetten naar leverancier
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="doorzetten-dialog">
        <DialogHeader><DialogTitle>Order doorzetten naar leverancier</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Leverancier</Label>
            <select value={f.leverancier_id} onChange={(e) => setF({ ...f, leverancier_id: e.target.value })}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm" data-testid="doorzetten-leverancier-select">
              <option value="">— Kies leverancier —</option>
              {leveranciers.map((l) => <option key={l.id} value={l.id}>{l.naam}{l.email ? "" : " (geen e-mail!)"}</option>)}
            </select>
            {gekozen && !gekozen.email && <p className="text-xs text-destructive mt-1">Deze leverancier heeft geen e-mailadres.</p>}
            {gekozen?.email && <p className="text-xs text-muted-foreground mt-1">Mail naar: {gekozen.email}</p>}
          </div>
          <div><Label>Gewenste aanleverdatum</Label>
            <Input type="date" value={f.aanleverdatum} onChange={(e) => setF({ ...f, aanleverdatum: e.target.value })} data-testid="doorzetten-aanleverdatum" /></div>
          <div>
            <Label>Voorraad afboeken van</Label>
            <div className="flex gap-2 mt-1">
              <Button type="button" variant={f.voorraad_bron === "leverancier" ? "default" : "outline"} size="sm"
                onClick={() => setF({ ...f, voorraad_bron: "leverancier" })} data-testid="doorzetten-bron-leverancier">Leverancier</Button>
              <Button type="button" variant={f.voorraad_bron === "warehouse" ? "default" : "outline"} size="sm"
                onClick={() => setF({ ...f, voorraad_bron: "warehouse" })} data-testid="doorzetten-bron-warehouse">Eigen warehouse</Button>
            </div>
          </div>
          <div><Label>Referentie / notitie (optioneel)</Label>
            <Textarea value={f.referentie} onChange={(e) => setF({ ...f, referentie: e.target.value })} placeholder="Bijv. PO-nummer of opmerking" /></div>
          <div className="text-xs text-muted-foreground bg-secondary/50 rounded-md p-3">
            Er wordt een pakbon (PDF) en een inkooporder (Excel) gegenereerd en met de klantgegevens naar de leverancier gemaild via de ingestelde Inkoop-afzender. Tevens komt een concept-factuur in de wachtrij.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} disabled={busy || !f.leverancier_id} data-testid="doorzetten-submit">
            {busy ? "Versturen…" : "Versturen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Artikelen beheren in één pop-up: producten aanvinken, daarna aantallen/prijzen aanpassen.
function ArtikelenDialog({ kansId, relatieId, isCampagne, regels, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [producten, setProducten] = useState([]);
  const [klant, setKlant] = useState({}); // product_id -> {verkoopprijs, inkoopprijs}
  const [zoek, setZoek] = useState("");
  const [sel, setSel] = useState({}); // sku -> regel-vorm
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/producten").then(r => setProducten(r.data || []));
    if (relatieId) api.get(`/organisaties/${relatieId}/prijzen`).then(r => {
      const m = {}; (r.data || []).forEach(x => { m[x.product_id] = x; }); setKlant(m);
    }).catch(() => {});
    const init = {};
    (regels || []).forEach(rg => {
      if (rg.code) init[rg.code] = {
        regelId: rg.id, code: rg.code, product: rg.product,
        aantal: rg.aantal, verkoopprijs: rg.verkoopprijs, inkoopprijs: rg.inkoopprijs,
        is_klantprijs: rg.is_klantprijs,
      };
    });
    setSel(init); setZoek("");
  }, [open]); // eslint-disable-line

  const toggle = (p) => setSel(prev => {
    const n = { ...prev };
    if (n[p.sku]) { delete n[p.sku]; return n; }
    const cust = klant[p.id];
    const useKlant = !isCampagne && cust && cust.verkoopprijs != null;
    n[p.sku] = {
      code: p.sku, product: p.naam, aantal: 1,
      verkoopprijs: useKlant ? Number(cust.verkoopprijs) : Number(p.verkoopprijs || 0),
      inkoopprijs: (cust && cust.inkoopprijs != null) ? Number(cust.inkoopprijs) : Number(p.inkoopprijs || 0),
      is_klantprijs: !!useKlant,
    };
    return n;
  });
  const upd = (sku, veld, val) => setSel(prev => ({ ...prev, [sku]: { ...prev[sku], [veld]: val } }));

  const gefilterd = useMemo(() => {
    const t = zoek.trim().toLowerCase();
    const base = producten.filter(p => !t || (p.naam || "").toLowerCase().includes(t) || (p.sku || "").toLowerCase().includes(t));
    return base.slice(0, 60);
  }, [zoek, producten]);

  const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
  const gekozen = Object.values(sel);
  const totaal = gekozen.reduce((s, r) => s + num(r.aantal) * num(r.verkoopprijs), 0);

  const opslaan = async () => {
    setBusy(true);
    try {
      const huidigCodes = new Set((regels || []).map(r => r.code));
      const nieuweCodes = new Set(gekozen.map(r => r.code));
      // verwijderen
      for (const r of (regels || [])) {
        if (!nieuweCodes.has(r.code)) await api.delete(`/verkoopkansregels/${r.id}`);
      }
      // toevoegen / bijwerken
      for (const r of gekozen) {
        const payload = { code: r.code, product: r.product, aantal: num(r.aantal),
                          verkoopprijs: num(r.verkoopprijs), inkoopprijs: num(r.inkoopprijs),
                          is_klantprijs: !!r.is_klantprijs };
        if (r.regelId) await api.patch(`/verkoopkansregels/${r.regelId}`, payload);
        else if (huidigCodes.has(r.code)) {/* zou niet voorkomen */}
        else await api.post("/verkoopkansregels", { ...payload, verkoopkans_id: kansId });
      }
      toast.success("Artikelen opgeslagen");
      setOpen(false); onSaved();
    } catch { toast.error("Opslaan mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-5xl w-[95vw]" data-testid="artikelen-dialog">
        <DialogHeader><DialogTitle>Artikelen beheren</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Links: aanvinkbare productenlijst */}
          <div>
            <Input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek product…"
              data-testid="artikel-zoek" autoComplete="off" />
            <div className="mt-2 border border-border rounded-lg max-h-[52vh] overflow-y-auto divide-y divide-border">
              {gefilterd.map(p => {
                const cust = klant[p.id];
                const useKlant = !isCampagne && cust && cust.verkoopprijs != null;
                const toonPrijs = useKlant ? Number(cust.verkoopprijs) : (Number(p.verkoopprijs) || 0);
                return (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-secondary cursor-pointer text-sm"
                  data-testid={`artikel-optie-${p.sku}`}>
                  <Checkbox checked={!!sel[p.sku]} onCheckedChange={() => toggle(p)} />
                  <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">{p.sku}</span>
                  <span className="flex-1 leading-snug">{p.naam}</span>
                  <span className="text-xs shrink-0 text-right">
                    <span className={useKlant ? "text-primary font-medium" : "text-muted-foreground"}>€ {toonPrijs.toFixed(2)}</span>
                    {useKlant && <span className="block text-[10px] text-muted-foreground">klantprijs</span>}
                  </span>
                </label>
              );})}
            </div>
          </div>
          {/* Rechts: gekozen met aantal + prijs */}
          <div>
            <div className="text-xs text-muted-foreground mb-2">
              {gekozen.length} geselecteerd{!isCampagne && " · standaard klantprijzen ingevuld"}
            </div>
            <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1">
              {gekozen.length === 0 && <div className="text-sm text-muted-foreground italic">Vink links artikelen aan.</div>}
              {gekozen.map(r => (
                <div key={r.code} className="border border-border rounded-lg p-2.5 space-y-1.5" data-testid={`artikel-gekozen-${r.code}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium leading-snug">{r.product}</div>
                    {r.is_klantprijs && <span className="text-[10px] uppercase font-bold bg-primary/15 text-primary rounded px-1.5 py-0.5 shrink-0">klantprijs</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Aantal</Label>
                      <Input type="number" min="0" step="1" value={r.aantal} className="h-8"
                        onChange={(e) => upd(r.code, "aantal", e.target.value)} data-testid={`artikel-aantal-${r.code}`} />
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Prijs (€)</Label>
                      <Input type="number" min="0" step="0.01" value={r.verkoopprijs} className="h-8 text-right"
                        onChange={(e) => upd(r.code, "verkoopprijs", e.target.value, true)}
                        data-testid={`artikel-prijs-${r.code}`} />
                    </div>
                    <div className="text-right w-20">
                      <Label className="text-[10px] text-muted-foreground">Regel</Label>
                      <div className="h-8 flex items-center justify-end text-sm tabular-nums">{fmtEUR(num(r.aantal) * num(r.verkoopprijs))}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="items-center">
          <div className="mr-auto text-sm font-medium" data-testid="artikelen-totaal">Totaal: {fmtEUR(totaal)}</div>
          <Button variant="outline" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={opslaan} disabled={busy} data-testid="artikelen-opslaan">{busy ? "Bezig…" : "Opslaan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VerkoopkansDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";

  const [kans, setKans] = useState(null);
  const [org, setOrg] = useState(null);
  const [contacten, setContacten] = useState([]);
  const [campagne, setCampagne] = useState(null);
  const [campagnes, setCampagnes] = useState([]);
  const [regels, setRegels] = useState([]);
  const [nav, setNav] = useState({ prev: null, next: null, totaal: 1, positie: 1 });
  const [notitie, setNotitie] = useState("");
  const [verwantePakbonnen, setVerwantePakbonnen] = useState([]);
  const [verwanteFacturen, setVerwanteFacturen] = useState([]);
  const [btw, setBtw] = useState(null);
  const [doorzettenAan, setDoorzettenAan] = useState(false);
  const [belOpen, setBelOpen] = useState(false);
  const [belletjes, setBelletjes] = useState([]);
  const [agendaNav, setAgendaNav] = useState({ vorige: null, volgende: null, positie: null, totaal: 0 });

  const reload = async () => {
    let k;
    try { ({ data: k } = await api.get(`/verkoopkansen/${id}`)); }
    catch { toast.error("Niet gevonden"); navigate("/agenda"); return; }
    setKans(k);
    const [{ data: r }, { data: n }, { data: c }, { data: pbs }, { data: fact }, { data: ct }] = await Promise.all([
      api.get(`/verkoopkansen/${id}/regels`),
      api.get(`/verkoopkansen/${id}/navigatie`),
      api.get("/campagnes"),
      api.get("/pakbonnen", { params: { verkoopkans_id: id } }),
      api.get("/verkoopfacturen", { params: { verkoopkans_id: id } }),
      api.get("/contactpersonen", { params: { organisatie_id: k.relatie_id } }),
    ]);
    setRegels(r);
    setNav(n);
    try { const { data: o } = await api.get(`/organisaties/${k.relatie_id}`); setOrg(o); } catch { setOrg(null); }
    setContacten(ct);
    setCampagnes(c);
    setCampagne(c.find(x => x.id === k.campagne_id) || null);
    setVerwantePakbonnen(pbs);
    setVerwanteFacturen(Array.isArray(fact) ? fact : []);
    try { const { data: b } = await api.get(`/verkoopkansen/${id}/btw`); setBtw(b); } catch (e) { console.warn("BTW ophalen mislukt:", e); }
    try { const { data: ab } = await api.get(`/verkoopkansen/${id}/agenda-buren`); setAgendaNav(ab); } catch (e) { console.warn("Agenda-buren ophalen mislukt:", e); }
    try { const { data: bl } = await api.get(`/verkoopkansen/${id}/belletjes`); setBelletjes(bl || []); } catch (e) { console.warn("Belletjes ophalen mislukt:", e); setBelletjes([]); }
    try { const { data: ec } = await api.get("/instellingen/email"); setDoorzettenAan(!!ec?.doorzetten_actief); } catch (e) { console.warn("E-mailinstellingen ophalen mislukt:", e); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);
  usePakketSync(() => reload());

  const patch = async (field, value) => {
    try {
      const { data } = await api.patch(`/verkoopkansen/${id}`, { [field]: value });
      setKans(data); toast.success("Opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const remove = async () => {
    if (!window.confirm("Verkoopkans verwijderen?")) return;
    await api.delete(`/verkoopkansen/${id}`);
    toast.success("Verwijderd");
    navigate("/verkoopkansen");
  };

  const belOrg = () => {
    const tel = (org?.telefoon || "").replace(/\s/g, "");
    if (tel) window.location.href = `tel:${tel}`;
    else toast.message("Geen telefoonnummer bekend — log de uitkomst handmatig");
    setBelOpen(true);
  };

  const toggleVoltooid = async () => {
    const v = !kans.voltooid;
    try {
      const { data } = await api.patch(`/verkoopkansen/${id}`, { voltooid: v, voltooid_op: v ? new Date().toISOString() : null });
      setKans(data);
      toast.success(v ? "Gemarkeerd als voltooid" : "Markering verwijderd");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const removeRegel = async (rid) => {
    if (!window.confirm("Deze orderregel verwijderen?")) return;
    await api.delete(`/verkoopkansregels/${rid}`);
    toast.success("Regel verwijderd"); reload();
  };

  const patchRegel = async (rid, veld, waarde) => {
    try {
      await api.patch(`/verkoopkansregels/${rid}`, { [veld]: waarde });
      // Optimistisch lokaal bijwerken (voorkomt flicker), volledige reload op de achtergrond
      setRegels(rs => rs.map(r => r.id === rid ? { ...r, [veld]: waarde } : r));
      toast.success("Regel bijgewerkt");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const addNotitie = async () => {
    if (!notitie.trim()) return;
    try {
      await api.post(`/verkoopkansen/${id}/notities`, { tekst: notitie });
      setNotitie(""); toast.success("Notitie toegevoegd"); reload();
    } catch { toast.error("Opslaan mislukt"); }
  };

  const totaalOmzet = useMemo(() =>
    regels.reduce((s, r) => s + (Number(r.aantal) || 0) * (Number(r.verkoopprijs) || 0), 0)
  , [regels]);
  const totaalMarge = useMemo(() =>
    regels.reduce((s, r) => s + (Number(r.aantal) || 0) * ((Number(r.verkoopprijs) || 0) - (Number(r.inkoopprijs) || 0)), 0)
  , [regels]);
  const margePct = totaalOmzet > 0 ? (totaalMarge / totaalOmzet) * 100 : 0;

  // Gecombineerde notities: handmatige notities + notities uit gelogde belletjes (intern)
  const alleNotities = useMemo(() => {
    const manueel = (kans?.notities || []).map(n => ({
      id: n.id, tekst: n.tekst, auteur: n.auteur, tijdstip: n.tijdstip, bron: "notitie",
    }));
    const uitBellen = (belletjes || []).filter(b => (b.notitie || "").trim()).map(b => ({
      id: b.id, tekst: b.notitie, auteur: b.gebruiker || b.auteur, tijdstip: b.tijdstip || b.created_at,
      bron: "belletje", uitkomst: b.uitkomst,
    }));
    return [...manueel, ...uitBellen].sort((a, b) => String(b.tijdstip || "").localeCompare(String(a.tijdstip || "")));
  }, [kans, belletjes]);

  // Houd bedrag-veld synchroon met regel-omzet (alleen als afwijkend, zonder extra toast)
  useEffect(() => {
    if (kans && regels.length > 0 && Math.abs((kans.bedrag || 0) - totaalOmzet) > 0.001) {
      // silent update — patchRegel toont al een toast bij inline edits
      api.patch(`/verkoopkansen/${id}`, { bedrag: totaalOmzet })
        .then(({ data }) => setKans(data))
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, [totaalOmzet]);

  // Navigatie: volg de volgorde van het verkoopkansen-overzicht als de gebruiker
  // daarvandaan kwam; anders terugvallen op campagne-wachtrij / agendavolgorde.
  const overz = leesOverzichtNav("verkoopkansen", id);
  const heeftOverz = overz.positie != null;
  const navPrev = heeftOverz ? overz.vorige : (nav.prev || agendaNav.vorige);
  const navNext = heeftOverz ? overz.volgende : (nav.next || agendaNav.volgende);
  const navPos = heeftOverz ? overz.positie : (nav.positie || agendaNav.positie);
  const navTot = heeftOverz ? overz.totaal : (nav.totaal || agendaNav.totaal);
  const toonNav = heeftOverz || campagne || agendaNav.positie;
  const terugPad = location.state?.terug || (heeftOverz && overz.terug ? overz.terug : "/agenda");
  const terugLabel = location.state?.terugLabel || (heeftOverz ? "Verkoopkansen" : "Agenda");
  usePijltjesNav("/verkoopkansen", navPrev, navNext);

  if (!kans) return <div className="text-muted-foreground">Laden…</div>;

  return (
    <div className="space-y-5" data-testid="kans-detail">
      {/* RIJ 1 — Navigatie wachtrij + voltooid + verwijderen */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(terugPad)} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> {terugLabel}
          </Button>
          {toonNav && (
            <div className="flex items-center gap-1.5 bg-card border border-border rounded-full px-2 py-1" data-testid="wachtrij-nav">
              <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="kans-prev"
                disabled={!navPrev}
                onClick={() => { navPrev && navigate(`/verkoopkansen/${navPrev}`); }}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums px-1">
                {navPos} / {navTot}
              </span>
              <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="kans-next"
                disabled={!navNext}
                onClick={() => { navNext && navigate(`/verkoopkansen/${navNext}`); }}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant={kans.voltooid ? "default" : "outline"} size="sm" onClick={toggleVoltooid} data-testid="kans-voltooid-toggle">
            <CheckCircle2 className="h-4 w-4 mr-1" />{kans.voltooid ? "Voltooid" : "Markeer als voltooid"}
          </Button>
          <Button variant="outline" size="sm" onClick={remove} data-testid="kans-verwijder-btn"
            className="text-destructive hover:text-destructive">
            <Trash2 className="h-4 w-4 mr-1" /> Verwijderen
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* RIJ 2/3/4/5/6 — kerngegevens */}
          <div className="bg-card border border-border rounded-xl p-4 sm:p-6 space-y-5">
            {/* RIJ 2: org-naam groot + contact + bel | omzet + marge */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0 space-y-2">
                {org ? (
                  <Link to={`/organisaties/${org.id}`} data-testid="kans-org-naam"
                    className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight inline-flex items-center gap-2 hover:underline break-words">
                    {org.naam} <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                  </Link>
                ) : <div className="text-2xl font-medium">—</div>}
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="min-w-[180px] flex-1 sm:flex-none sm:min-w-[220px]">
                    <Select value={kans.contactpersoon_id || "none"} onValueChange={(v)=>patch("contactpersoon_id", v === "none" ? null : v)}>
                      <SelectTrigger className="h-10 sm:h-9" data-testid="kans-contactpersoon"><SelectValue placeholder="Contactpersoon" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— geen contactpersoon —</SelectItem>
                        {contacten.map(c => <SelectItem key={c.id} value={c.id}>{[c.voornaam, c.achternaam].filter(Boolean).join(" ") || c.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={belOrg} data-testid="bel-org-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white h-10 sm:h-9">
                    <PhoneCall className="h-4 w-4 mr-1.5" /> Bellen
                  </Button>
                </div>
              </div>
              <div className="flex gap-4 sm:gap-6 text-right w-full sm:w-auto justify-end">
                <div>
                  <div className="text-[10px] sm:text-xs uppercase text-muted-foreground tracking-wide">Omzet</div>
                  <div className="font-display text-xl sm:text-2xl font-medium tabular-nums" data-testid="kans-omzet">{fmtEUR(totaalOmzet || kans.bedrag || 0)}</div>
                </div>
                <div>
                  <div className="text-[10px] sm:text-xs uppercase text-muted-foreground tracking-wide">Marge</div>
                  <div className="font-display text-xl sm:text-2xl font-medium text-emerald-600 tabular-nums" data-testid="kans-marge">{fmtEUR(totaalMarge)}</div>
                  <div className="text-[10px] sm:text-xs text-muted-foreground">{margePct.toFixed(1)}%</div>
                </div>
              </div>
            </div>

            {/* RIJ 3: telefoon · email · bezoekadres */}
            <div className="flex items-center gap-4 flex-wrap text-sm pt-3 border-t border-border">
              {org?.telefoon ? (
                <a href={`tel:${org.telefoon.replace(/\s/g,"")}`} className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid="org-tel">
                  <Phone className="h-3.5 w-3.5" /> {org.telefoon}
                </a>
              ) : <span className="text-muted-foreground inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> —</span>}
              {org?.email ? (
                <a href={`mailto:${org.email}`} className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid="org-mail">
                  <Mail className="h-3.5 w-3.5" /> {org.email}
                </a>
              ) : <span className="text-muted-foreground inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> —</span>}
              <span className="inline-flex items-center gap-1.5 text-muted-foreground" data-testid="org-bezoekadres">
                <MapPin className="h-3.5 w-3.5" />
                {[ [org?.straat, org?.huisnummer].filter(Boolean).join(" "), [org?.postcode, org?.plaats].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "Geen bezoekadres"}
              </span>
            </div>

            {/* RIJ 4: verkoopproces + percentage + markeer afgevallen */}
            <div className="pt-3 border-t border-border">
              <FaseStepper value={kans.fase || "Cold"} onChange={(v)=>patch("fase", v)} />
            </div>

            {/* RIJ 5: verkoopdatum (auto bij Deal) */}
            <div className="pt-3 border-t border-border grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Verkoopdatum {kans.fase !== "Deal!" && <span className="text-[10px]">(automatisch bij Deal)</span>}</Label>
                <Input type="date" data-testid="kans-verkoopdatum"
                  value={toDateInput(kans.verkoopdatum)}
                  onChange={(e)=>patch("verkoopdatum", e.target.value ? new Date(e.target.value).toISOString() : null)} />
              </div>
              {/* RIJ 6: planner campagne — wanneer opvolgen */}
              <div>
                <Label className="text-xs text-muted-foreground">Opvolgen op (Planner)</Label>
                <Input type="datetime-local" data-testid="kans-gepland-op"
                  value={kans.gepland_op ? new Date(kans.gepland_op).toLocaleString("sv-SE").slice(0, 16).replace(" ", "T") : ""}
                  onChange={(e)=>patch("gepland_op", e.target.value ? new Date(e.target.value).toISOString() : null)} />
                <label className="flex items-center gap-2 text-xs mt-2 cursor-pointer select-none" htmlFor="kans-herinnering-toggle">
                  <Checkbox id="kans-herinnering-toggle" checked={kans.in_herinneringen !== false} onCheckedChange={(v)=>patch("in_herinneringen", v === true)} data-testid="kans-in-herinneringen" />
                  <span>In Apple Herinneringen tonen</span>
                </label>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Campagne</Label>
                <Select value={kans.campagne_id || "none"} onValueChange={(v)=>patch("campagne_id", v === "none" ? null : v)}>
                  <SelectTrigger data-testid="kans-campagne"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— geen —</SelectItem>
                    {campagnes.map(c => <SelectItem key={c.id} value={c.id}>{c.naam}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* RIJ 7: Artikelen (multi-select pop-up) */}
          <div className="bg-card border border-border rounded-xl">
            <div className="p-4 flex items-center justify-between border-b border-border">
              <div>
                <div className="font-medium">Artikelen ({regels.length})</div>
                <div className="text-xs text-muted-foreground">Omzet {fmtEUR(totaalOmzet)} · Marge {fmtEUR(totaalMarge)}</div>
              </div>
              <ArtikelenDialog kansId={id} relatieId={kans.relatie_id} isCampagne={!!kans.campagne_id}
                regels={regels} onSaved={reload}
                trigger={<Button size="sm" data-testid="beheer-artikelen-btn"><Plus className="h-4 w-4 mr-1" />Artikelen selecteren</Button>} />
            </div>
            {regels.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Nog geen artikelen. Klik op "Artikelen selecteren".</div>
            ) : (
              <>
              {/* Desktop: tabel met inline edit */}
              <table className="w-full text-sm hidden md:table" data-testid="regels-tabel">
                <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                  <tr>
                    <th className="text-left p-3">Code</th><th className="text-left p-3">Product</th>
                    <th className="text-right p-3 w-24">Aantal</th><th className="text-right p-3 w-32">Verkoop</th><th className="text-right p-3 w-28">Totaal</th>
                    <th className="p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {regels.map(r => (
                    <tr key={r.id} data-testid={`regel-${r.id}`}>
                      <td className="p-3 font-mono text-xs">{r.code}</td>
                      <td className="p-3">{r.product}</td>
                      <td className="p-3 text-right">
                        <Input type="number" step="1" min="0" defaultValue={r.aantal}
                          className="h-8 text-right w-20 ml-auto"
                          data-testid={`regel-aantal-${r.id}`}
                          onBlur={(e) => {
                            const v = parseFloat(e.target.value);
                            if (!isNaN(v) && v !== r.aantal) patchRegel(r.id, "aantal", v);
                          }} />
                      </td>
                      <td className="p-3 text-right">
                        <Input type="number" step="0.01" min="0" defaultValue={r.verkoopprijs}
                          className="h-8 text-right w-28 ml-auto"
                          data-testid={`regel-prijs-${r.id}`}
                          onBlur={(e) => {
                            const v = parseFloat(e.target.value);
                            if (!isNaN(v) && v !== r.verkoopprijs) patchRegel(r.id, "verkoopprijs", v);
                          }} />
                      </td>
                      <td className="p-3 text-right font-medium tabular-nums">{fmtEUR((r.aantal||0)*(r.verkoopprijs||0))}</td>
                      <td className="p-3 text-right">
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => removeRegel(r.id)} data-testid={`regel-verwijder-${r.id}`} title="Regel verwijderen">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobiel: kaart per regel met grote tap targets */}
              <div className="md:hidden divide-y divide-border" data-testid="regels-mobiel">
                {regels.map(r => (
                  <div key={r.id} className="p-3 space-y-2" data-testid={`regel-kaart-${r.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-xs text-muted-foreground">{r.code}</div>
                        <div className="font-medium text-sm leading-snug break-words">{r.product}</div>
                      </div>
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                        onClick={() => removeRegel(r.id)} data-testid={`regel-mobiel-verwijder-${r.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 items-end">
                      <div>
                        <Label className="text-[10px] uppercase text-muted-foreground">Aantal</Label>
                        <Input type="number" step="1" min="0" defaultValue={r.aantal}
                          inputMode="numeric" className="h-10 text-base"
                          data-testid={`regel-mobiel-aantal-${r.id}`}
                          onBlur={(e) => {
                            const v = parseFloat(e.target.value);
                            if (!isNaN(v) && v !== r.aantal) patchRegel(r.id, "aantal", v);
                          }} />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-muted-foreground">Prijs €</Label>
                        <Input type="number" step="0.01" min="0" defaultValue={r.verkoopprijs}
                          inputMode="decimal" className="h-10 text-base"
                          data-testid={`regel-mobiel-prijs-${r.id}`}
                          onBlur={(e) => {
                            const v = parseFloat(e.target.value);
                            if (!isNaN(v) && v !== r.verkoopprijs) patchRegel(r.id, "verkoopprijs", v);
                          }} />
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase text-muted-foreground">Totaal</div>
                        <div className="text-base font-medium tabular-nums">{fmtEUR((r.aantal||0)*(r.verkoopprijs||0))}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>

          {/* RIJ 8: Ordernummer · Leveranciers-ID · Planner levering */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="text-xs uppercase text-muted-foreground tracking-wide mb-3">Order & levering</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Ordernummer</Label>
                <Input value={kans.ordernummer || ""} onChange={(e)=>setKans({...kans, ordernummer: e.target.value})}
                  onBlur={(e)=>patch("ordernummer", e.target.value)} data-testid="kans-ordernummer" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Referentie</Label>
                <Input value={kans.referentie || ""} onChange={(e)=>setKans({...kans, referentie: e.target.value})}
                  onBlur={(e)=>patch("referentie", e.target.value)} data-testid="kans-referentie" placeholder="Klantreferentie/kenmerk" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Leveranciers-ID</Label>
                <Input value={kans.leveranciers_id || ""} onChange={(e)=>setKans({...kans, leveranciers_id: e.target.value})}
                  onBlur={(e)=>patch("leveranciers_id", e.target.value)} data-testid="kans-levid" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Leverdatum</Label>
                <Input type="date" data-testid="kans-leverdatum" value={toDateInput(kans.leverdatum)}
                  onChange={(e)=>patch("leverdatum", e.target.value ? new Date(e.target.value).toISOString() : null)} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Lever-timeslot</Label>
                <Select value={kans.lever_timeslot || "geen"} onValueChange={(v)=>patch("lever_timeslot", v)}>
                  <SelectTrigger data-testid="kans-lever-timeslot"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="geen">Geen</SelectItem>
                    {Array.from({ length: 18 }, (_, i) => { const m = 540 + i * 30; return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; })
                      .map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Leverdatum en timeslot worden meegenomen op de pakbon en factuur.</p>
          </div>

          {/* RIJ 9: aanmaakdatum · laatst gewijzigd door */}
          <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-6 text-sm flex-wrap" data-testid="kans-meta">
            <div><span className="text-muted-foreground">Aangemaakt: </span>{fmtDateTime(kans.created_at)}</div>
            <div><span className="text-muted-foreground">Laatst gewijzigd door: </span>{kans.gewijzigd_door || "—"}{kans.updated_at ? ` · ${fmtDateTime(kans.updated_at)}` : ""}</div>
          </div>

          {/* Notities (handmatig + uit call-logging, altijd intern) */}
          <div className="bg-card border border-border rounded-xl">
            <div className="p-4 border-b border-border font-medium flex items-center gap-2">
              Notities <span className="text-xs font-normal text-muted-foreground">(intern — incl. notities uit gelogde belletjes)</span>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                <Textarea value={notitie} onChange={(e)=>setNotitie(e.target.value)} placeholder="Voeg een interne notitie toe…" data-testid="nieuwe-notitie" rows={2} />
                <Button onClick={addNotitie} data-testid="add-notitie">Toevoegen</Button>
              </div>
              <div className="space-y-2">
                {alleNotities.map(n => (
                  <div key={n.id} className="border border-border rounded-md p-3 bg-secondary/30" data-testid={`notitie-${n.id}`}>
                    <div className="text-sm whitespace-pre-wrap">{n.tekst}</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                      {n.bron === "belletje" && <Badge variant="outline" className="text-[10px]"><PhoneCall className="h-2.5 w-2.5 mr-1" />{n.uitkomst || "Belletje"}</Badge>}
                      {n.auteur || "—"} · {fmtDateTime(n.tijdstip)}
                    </div>
                  </div>
                ))}
                {alleNotities.length === 0 && <div className="text-sm text-muted-foreground italic">Nog geen notities.</div>}
              </div>
            </div>
          </div>
        </div>

        {/* Rechter kolom: bellen + documenten/acties */}
        <div className="space-y-4">
          <BelletjesPanel kansId={id} open={belOpen} onOpenChange={setBelOpen} onLogged={reload} />
          {btw && (
            <div className="bg-card border border-border rounded-xl p-5">
              <BtwOverzicht result={btw} />
            </div>
          )}
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wide">Documenten & acties</div>
            {["Deal!", "Betaald, nog niet verstuurd"].includes(kans.fase) ? (
              <PakbonDialog kansId={id} onCreated={() => reload()} />
            ) : (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-3">
                Pakbon kan alleen bij fase <Badge variant="outline">Deal!</Badge> of een betaalde webshoporder
              </div>
            )}
            {kans.fase === "Deal!" && doorzettenAan && (
              <DoorzetLeverancierDialog kansId={id} kansReferentie={kans.referentie || ""} onDone={() => reload()} />
            )}
            {kans.fase === "Deal!" && (
              <DoorzetChannelDockDialog kansId={id} kansReferentie={kans.referentie || ""}
                info={kans.channeldock} onDone={() => reload()} />
            )}

            <div className="space-y-2 pt-3 border-t border-border">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">Samplebrief</div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="flex-1"
                  onClick={() => downloadFile(`/verkoopkansen/${id}/samplebrief.docx`, `samplebrief-${(kans.naam || id.slice(0, 8)).replace(/[^\w-]+/g, "_")}.docx`).catch(() => toast.error("Download mislukt"))}
                  data-testid="samplebrief-word-btn">
                  Word
                </Button>
                <Button variant="outline" size="sm" className="flex-1"
                  onClick={() => downloadFile(`/verkoopkansen/${id}/samplebrief.docx?format=pdf`, `samplebrief-${(kans.naam || id.slice(0, 8)).replace(/[^\w-]+/g, "_")}.pdf`).catch(() => toast.error("PDF genereren mislukt"))}
                  data-testid="samplebrief-pdf-btn">
                  PDF
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Gebruikt automatisch het samplebrief-template van de gekoppelde campagne (indien ingesteld).</p>
            </div>

            {verwantePakbonnen.length > 0 && (
              <div className="space-y-2 pt-3 border-t border-border">
                <div className="text-xs uppercase text-muted-foreground tracking-wide">Pakbon(nen)</div>
                {verwantePakbonnen.map(pb => (
                  <div key={pb.id} className="flex items-center justify-between text-sm bg-secondary/30 rounded-md p-2" data-testid={`pakbon-${pb.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium">{pb.nummer}</div>
                      <div className="text-xs text-muted-foreground">{fmtDate(pb.leverdatum)}</div>
                      {pb.pakket_status && (
                        <div className="mt-1"><PakketStatusBadge status={pb.pakket_status}
                          omschrijving={pb.pakket_status_omschrijving} tracktraceUrl={pb.tracktrace_url} size="xs" /></div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => downloadFile(`/pakbonnen/${pb.id}/docx`, `pakbon-${pb.nummer}.docx`)}
                         className="inline-flex items-center text-primary hover:underline text-xs"><FileDown className="h-3 w-3 mr-1" /> Word</button>
                      <button onClick={() => downloadFile(`/pakbonnen/${pb.id}/docx?format=pdf`, `pakbon-${pb.nummer}.pdf`)}
                         className="inline-flex items-center text-muted-foreground hover:text-primary hover:underline text-xs"><FileDown className="h-3 w-3 mr-1" /> PDF</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2 pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase text-muted-foreground tracking-wide">Facturen ({verwanteFacturen.length})</div>
                <NieuweHandmatigeFactuurButton kansId={id} onCreated={() => reload()} />
              </div>
              {verwanteFacturen.length === 0 && (
                <div className="text-xs text-muted-foreground italic">Nog geen facturen — worden automatisch aangemaakt bij een pakbon, of handmatig via de knop hierboven.</div>
              )}
              {verwanteFacturen.map((vf) => {
                const isCredit = vf.type === "creditfactuur";
                return (
                  <div key={vf.id} className={`rounded-md p-2 ${isCredit ? "bg-rose-50 border border-rose-200" : "bg-secondary/30"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium text-sm truncate">{vf.factuurnummer || "(concept)"}</span>
                          {isCredit && <Badge variant="outline" className="text-rose-700 border-rose-300 text-[10px]">Credit</Badge>}
                          {vf.handmatig_aangemaakt && <Badge variant="outline" className="text-[10px]">Handmatig</Badge>}
                        </div>
                        <Badge variant={vf.status_factuur === "gefactureerd" ? "default" : "secondary"} className="mt-1 text-xs">
                          {vf.status_factuur === "gefactureerd" ? "Gefactureerd" : vf.status_factuur === "betaald" ? "Betaald" : "In wachtrij"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => downloadFile(`/verkoopfacturen/${vf.id}/docx`, `${isCredit ? "creditfactuur" : "factuur"}-${vf.factuurnummer||vf.id.slice(0,8)}.docx`)}
                           className="inline-flex items-center text-primary hover:underline text-xs" data-testid={`factuur-word-${vf.id}`}><FileDown className="h-3 w-3 mr-1" /> Word</button>
                        <button onClick={() => downloadFile(`/verkoopfacturen/${vf.id}/docx?format=pdf`, `${isCredit ? "creditfactuur" : "factuur"}-${vf.factuurnummer||vf.id.slice(0,8)}.pdf`)}
                           className="inline-flex items-center text-muted-foreground hover:text-primary hover:underline text-xs" data-testid={`factuur-pdf-${vf.id}`}><FileDown className="h-3 w-3 mr-1" /> PDF</button>
                      </div>
                    </div>
                    <Link to={`/verkoopfacturen/${vf.id}`} className="text-xs text-primary hover:underline mt-2 inline-flex items-center" data-testid={`factuur-open-${vf.id}`}>
                      Open factuur <ExternalLink className="h-3 w-3 ml-1" />
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {campagne && nav.next && (
        <div className="flex justify-end pt-2">
          <Button size="lg" onClick={() => navigate(`/verkoopkansen/${nav.next}`)} data-testid="footer-next-kans">
            Volgende kans in wachtrij ({nav.positie + 1} / {nav.totaal})
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}
      {campagne && !nav.next && (
        <div className="flex justify-end pt-2">
          <Link to={`/campagnes/${campagne.id}`}>
            <Button size="lg" variant="outline" data-testid="footer-back-campagne">
              <ArrowLeft className="h-4 w-4 mr-2" /> Einde wachtrij — terug naar campagne
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
